import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Progress } from '../ui/progress';
import { Activity, Trophy, Target, TrendingUp, TrendingDown, Coins, Award, Flame, Zap, History } from 'lucide-react';

export function DashboardOverviewTab({ stats, loading }) {
    if (loading) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 8 }, (_, i) => `dash-skel-${i}`).map((sk) => (
                    <Skeleton key={sk} className="h-28 bg-white/5" />
                ))}
            </div>
        );
    }
    if (!stats) return null;

    return (
        <>
            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-white/[0.02] border-white/5 hover:border-[#00FF66]/20 transition-colors card-hover animate-fade-in-up delay-1">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <Activity className="w-5 h-5 text-[#00FF66]" />
                        </div>
                        <div className="font-mono text-3xl font-bold text-white stat-number">{stats.total_bets}</div>
                        <div className="text-xs text-white/40 mt-1">Total Bets</div>
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5 hover:border-[#00FF66]/20 transition-colors card-hover animate-fade-in-up delay-2">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <Trophy className="w-5 h-5 text-[#FFD700]" />
                            <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-xs">{stats.win_rate}%</Badge>
                        </div>
                        <div className="font-mono text-3xl font-bold text-[#00FF66] stat-number">{stats.wins}</div>
                        <div className="text-xs text-white/40 mt-1">Wins</div>
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5 hover:border-red-500/20 transition-colors card-hover animate-fade-in-up delay-3">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <Target className="w-5 h-5 text-red-400" />
                        </div>
                        <div className="font-mono text-3xl font-bold text-red-400 stat-number">{stats.losses}</div>
                        <div className="text-xs text-white/40 mt-1">Losses</div>
                    </CardContent>
                </Card>

                <Card className={`bg-white/[0.02] border-white/5 hover:border-${stats.net_profit >= 0 ? '[#00FF66]' : 'red-500'}/20 transition-colors card-hover animate-fade-in-up delay-4`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            {stats.net_profit >= 0 ? (
                                <TrendingUp className="w-5 h-5 text-[#00FF66]" />
                            ) : (
                                <TrendingDown className="w-5 h-5 text-red-400" />
                            )}
                            <Badge className={`text-xs ${stats.roi >= 0 ? 'bg-[#00FF66]/20 text-[#00FF66]' : 'bg-red-400/20 text-red-400'}`}>
                                ROI {stats.roi >= 0 ? '+' : ''}{stats.roi || 0}%
                            </Badge>
                        </div>
                        <div className={`font-mono text-2xl font-bold stat-number ${stats.net_profit >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>
                            {stats.net_profit >= 0 ? '+' : ''}{stats.net_profit.toFixed(4)}
                        </div>
                        <div className="text-xs text-white/40 mt-1">Net Profit (ETH)</div>
                    </CardContent>
                </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Coins className="w-4 h-4 text-white/60" />
                            <span className="text-xs text-white/40">Total Staked</span>
                        </div>
                        <div className="font-mono text-xl font-bold text-white">{stats.total_staked.toFixed(4)} ETH</div>
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Award className="w-4 h-4 text-[#FFD700]" />
                            <span className="text-xs text-white/40">Biggest Win</span>
                        </div>
                        <div className="font-mono text-xl font-bold text-[#00FF66]">+{stats.biggest_win.toFixed(4)} ETH</div>
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Flame className="w-4 h-4 text-orange-400" />
                            <span className="text-xs text-white/40">Current Streak</span>
                        </div>
                        <div className={`font-mono text-xl font-bold ${stats.current_streak >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>
                            {stats.current_streak >= 0 ? `${stats.current_streak} wins` : `${Math.abs(stats.current_streak)} losses`}
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-4 h-4 text-[#9B6FE8]" />
                            <span className="text-xs text-white/40">Best Streak</span>
                        </div>
                        <div className="font-mono text-xl font-bold text-[#9B6FE8]">{stats.best_streak} in a row</div>
                    </CardContent>
                </Card>
            </div>

            {/* Win Rate Progress */}
            <Card className="bg-white/[0.02] border-white/5 mb-6">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-semibold text-white">Win Rate</h3>
                            <p className="text-sm text-white/40">Your overall performance</p>
                        </div>
                        <div className="text-3xl font-bold text-[#00FF66] font-mono">{stats.win_rate}%</div>
                    </div>
                    <Progress value={stats.win_rate} className="h-3 bg-white/10" />
                    <div className="flex justify-between mt-2 text-xs text-white/40">
                        <span>{stats.wins} wins</span>
                        <span>{stats.losses} losses</span>
                    </div>
                </CardContent>
            </Card>

            {/* Recent Results */}
            {stats.recent_results?.length > 0 && (
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <History className="w-5 h-5 text-[#00FF66]" />
                            Recent Results
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex gap-2">
                            {stats.recent_results.map((result, i) => (
                                <Link key={result.bet_id || `res-${i}`} to={`/bets/${result.bet_id}`}>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold ${
                                        result.result === 'win' ? 'bg-[#00FF66]/20 text-[#00FF66]' : 'bg-red-400/20 text-red-400'
                                    }`}>
                                        {result.result === 'win' ? 'W' : 'L'}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    );
}
